import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../api/axios";
import AdminLoadingOverlay from "../components/AdminLoadingOverlay";
import AdminShell from "../components/AdminShell";
import { useAuth } from "../context/AuthContext";
import "../styles/admin.css";

const RANGE_OPTIONS = [
  { value: "today", label: "Today" },
  { value: "this_week", label: "This Week" },
  { value: "this_month", label: "This Month" },
  { value: "this_year", label: "This Year" },
  { value: "custom", label: "Custom" },
];

const TABS = [
  { key: "business", label: "Overview" },
  { key: "bookings", label: "Bookings" },
  { key: "revenue", label: "Revenue" },
  { key: "fleet", label: "Vehicle Utilization" },
  { key: "customers", label: "Customers" },
  { key: "routes", label: "Routes" },
  { key: "operations", label: "Operations" },
  { key: "recommendations", label: "Recommendations" },
  { key: "reports", label: "Reports" },
];

const STATUS_LABELS = {
  awaitingPayment: "Awaiting Payment",
  paymentVerification: "Payment Verification",
  confirmed: "Confirmed",
  activeOngoing: "Active / Ongoing",
  completed: "Completed",
  cancelled: "Cancelled",
  additionalPaymentRequired: "Additional Payment Required",
};

function formatNumber(value) {
  return Math.round(Number(value || 0)).toLocaleString();
}

function formatCurrency(value) {
  return `PHP ${Math.round(Number(value || 0)).toLocaleString()}`;
}

function formatPercent(value) {
  return `${Math.round(Number(value || 0))}%`;
}

function formatHours(value) {
  const number = Number(value || 0);
  if (!number) return "0 hrs";
  return `${number.toLocaleString(undefined, { maximumFractionDigits: 1 })} hrs`;
}

function formatDate(value) {
  const parsed = value ? new Date(value) : null;
  if (!parsed || Number.isNaN(parsed.getTime())) return "N/A";
  return parsed.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function getRangeLabel(range) {
  if (!range) return "Selected range";
  return `${formatDate(range.startDate)} - ${formatDate(range.endDate)}`;
}

function getMax(items, key) {
  return Math.max(...items.map((item) => Number(item?.[key] || 0)), 1);
}

function toArray(value) {
  return Array.isArray(value) ? value : [];
}

function fillTrendGaps(data, startIso, endIso, valueKeys = []) {
  if (!startIso || !endIso) return data;
  const start = new Date(startIso);
  const end = new Date(endIso);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || start > end) return data;

  const dayMs = 24 * 60 * 60 * 1000;
  const maxDays = 366;
  const totalDays = Math.min(Math.ceil((end - start) / dayMs) + 1, maxDays);
  if (totalDays > 93) return data;

  const existing = new Map(data.map((item) => [item.key, item]));
  const filled = [];
  for (let i = 0; i < totalDays; i++) {
    const date = new Date(start.getTime() + i * dayMs);
    const key = date.toISOString().slice(0, 10);
    const label = date.toLocaleString("en-US", { month: "short", day: "numeric" });
    const base = { key, label };
    valueKeys.forEach((k) => { base[k] = 0; });
    filled.push(existing.has(key) ? { ...base, ...existing.get(key) } : base);
  }
  return filled;
}

function alertTone(value, tone) {
  return Number(value || 0) > 0 ? tone : "default";
}

function getPriorityTone(priority) {
  const value = String(priority || "").toLowerCase();
  if (value === "high") return "danger";
  if (value === "medium") return "warning";
  return "info";
}

const PRIORITY_META = {
  high: { label: "High Priority", tone: "danger", rank: 0 },
  medium: { label: "Medium Priority", tone: "warning", rank: 1 },
  low: { label: "Low Priority", tone: "info", rank: 2 },
};

function normalizePriority(priority) {
  const value = String(priority || "").toLowerCase();
  if (value.includes("high") || value === "danger") return "high";
  if (value.includes("medium") || value === "warning") return "medium";
  return "low";
}

function getRecommendationText(item = {}) {
  return [
    item.type,
    item.category,
    item.title,
    item.insight,
    item.message,
    item.reason,
    item.suggestedAction,
    item.supportingMetric,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

function getRecommendationPreset(item = {}) {
  const text = getRecommendationText(item);

  if (text.includes("cancel")) {
    return {
      category: "Operations",
      whyThisMatters: "Elevated cancellations can reduce confirmed revenue, leave vehicles idle, and create extra review work for the team.",
      recommendedAction: "Review payment deadlines, approval handoffs, and cancellation reasons to identify where customers are dropping off.",
      nextStep: "Review cancellation reasons",
      supportingMetric: item.supportingMetric || "Cancellation activity detected",
    };
  }

  if (text.includes("payment") || text.includes("invoice") || text.includes("revenue")) {
    return {
      category: "Revenue",
      whyThisMatters: "Payment friction can delay confirmed bookings and make revenue less predictable.",
      recommendedAction: "Check pending payments, proof review timing, invoice status, and payment reminders for bookings in this period.",
      nextStep: "Review bookings and revenue",
      supportingMetric: item.supportingMetric || "Payment or revenue activity detected",
    };
  }

  if (text.includes("underused") || text.includes("underutilized") || text.includes("no bookings") || text.includes("least booked")) {
    return {
      category: item.category || "Marketing",
      whyThisMatters: "Low utilization ties up fleet supply and may signal pricing, visibility, or listing quality issues.",
      recommendedAction: "Refresh the vehicle listing photos/details, consider a promo campaign, or review pricing to improve demand.",
      nextStep: "Review vehicle listing or create promo",
      supportingMetric: item.supportingMetric || "Low booking activity detected",
    };
  }

  if (text.includes("route") || text.includes("destination") || text.includes("pickup")) {
    return {
      category: item.category || "Route Demand",
      whyThisMatters: "Recurring route demand can reveal where FleetX should position vehicles, drivers, and promotions.",
      recommendedAction: "Inspect route demand, confirm vehicle availability for the route, and consider a targeted campaign if demand continues.",
      nextStep: "Inspect route demand",
      supportingMetric: item.supportingMetric || "Route demand activity detected",
    };
  }

  if (
    text.includes("sedan") ||
    text.includes("suv") ||
    text.includes("van") ||
    text.includes("mpv") ||
    text.includes("fleet") ||
    text.includes("category demand")
  ) {
    return {
      category: item.category || "Fleet Expansion",
      whyThisMatters: "Sustained category demand can lead to limited availability and missed bookings if supply is not aligned.",
      recommendedAction: "Review category availability and consider reallocating vehicles or adding another unit if demand continues.",
      nextStep: "Review vehicle availability",
      supportingMetric: item.supportingMetric || "Category demand detected",
    };
  }

  if (text.includes("customer") || text.includes("verification") || text.includes("document")) {
    return {
      category: item.category || "Operations",
      whyThisMatters: "Verification delays can slow booking conversion and increase customer support follow-up.",
      recommendedAction: "Review pending and expiring verifications, then prioritize accounts blocking active booking requests.",
      nextStep: "Review customer verification",
      supportingMetric: item.supportingMetric || "Customer verification activity detected",
    };
  }

  return {
    category: item.category || "Operations",
    whyThisMatters: "This signal may affect booking conversion, fleet availability, or admin workload if it continues.",
    recommendedAction: item.suggestedAction || "Review the related records and decide whether a pricing, fleet, or operations adjustment is needed.",
    nextStep: item.suggestedAction || "Review related records",
    supportingMetric: item.supportingMetric || "Analytics signal detected",
  };
}

function getRecommendationActions(item = {}) {
  const text = getRecommendationText(item);

  if (text.includes("cancel") || text.includes("payment") || text.includes("invoice") || text.includes("revenue")) {
    return [
      { label: "Review Bookings", href: "/bookings" },
      { label: "View Revenue", tab: "revenue" },
    ];
  }

  if (text.includes("underused") || text.includes("underutilized") || text.includes("no bookings") || text.includes("least booked")) {
    return [
      { label: "Review Vehicles", href: "/vehicles" },
      { label: "Create Promo", href: "/marketing-campaigns" },
    ];
  }

  if (text.includes("route") || text.includes("destination") || text.includes("pickup")) {
    return [
      { label: "View Routes", tab: "routes" },
      { label: "Create Campaign", href: "/marketing-campaigns" },
    ];
  }

  if (
    text.includes("sedan") ||
    text.includes("suv") ||
    text.includes("van") ||
    text.includes("mpv") ||
    text.includes("fleet") ||
    text.includes("category demand")
  ) {
    return [
      { label: "Review Vehicles", href: "/vehicles" },
      { label: "View Utilization", tab: "fleet" },
    ];
  }

  if (text.includes("customer") || text.includes("verification") || text.includes("document")) {
    return [{ label: "Review Users", href: "/users" }];
  }

  return [{ label: "Review Operations", href: "/bookings" }];
}

function getMetricStrength(item = {}) {
  const text = String(item.supportingMetric || item.metric || item.value || "");
  const matches = text.match(/\d+(\.\d+)?/g);
  if (!matches) return 0;
  return Math.max(...matches.map((value) => Number(value || 0)));
}

function buildRecommendationView(item = {}) {
  const priorityKey = normalizePriority(item.priority || item.tone);
  const priority = PRIORITY_META[priorityKey];
  const preset = getRecommendationPreset(item);
  const tone = priority.tone || getPriorityTone(priorityKey);

  return {
    ...item,
    priorityKey,
    priorityLabel: priority.label,
    tone,
    category: item.category || preset.category,
    title: item.title || "Operational Recommendation",
    insight: item.insight || item.message || "FleetX detected an analytics signal that needs admin review.",
    whyThisMatters: item.whyThisMatters || item.impact || preset.whyThisMatters || item.reason,
    recommendedAction: item.recommendedAction || item.prescriptiveAction || preset.recommendedAction || item.suggestedAction,
    nextStep: item.nextStep || item.suggestedNextStep || preset.nextStep || item.suggestedAction,
    supportingMetric: item.supportingMetric || item.metric || preset.supportingMetric,
    actions: getRecommendationActions(item),
    metricStrength: getMetricStrength(item),
  };
}

function compareRecommendations(a, b) {
  const aPriority = PRIORITY_META[a.priorityKey]?.rank ?? 2;
  const bPriority = PRIORITY_META[b.priorityKey]?.rank ?? 2;
  if (aPriority !== bPriority) return aPriority - bPriority;
  return Number(b.metricStrength || 0) - Number(a.metricStrength || 0);
}

function RecommendationCard({ item, compact = false, onAction }) {
  return (
    <article className={`exec-insight-card exec-insight-card--${item.tone} exec-insight-card--priority-${item.priorityKey}`}>
      <div className="exec-insight-card__top">
        <span className={`exec-insight-priority exec-insight-priority--${item.tone}`}>
          {item.priorityLabel}
        </span>
        {item.category ? <span className="exec-insight-category">{item.category}</span> : null}
      </div>
      <h4 className="exec-insight-card__title">{item.title}</h4>
      <div className="exec-insight-card__section">
        <span>Insight</span>
        <p>{item.insight}</p>
      </div>
      {!compact ? (
        <div className="exec-insight-card__section">
          <span>Why this matters</span>
          <p>{item.whyThisMatters}</p>
        </div>
      ) : null}
      <div className="exec-insight-card__action">
        <span>Recommended action</span>
        <p>{item.recommendedAction}</p>
      </div>
      {!compact ? (
        <>
          <div className="exec-insight-card__section exec-insight-card__section--next">
            <span>Suggested next step</span>
            <p>{item.nextStep}</p>
            {item.actions?.length ? (
              <div className="exec-insight-card__buttons">
                {item.actions.map((action) => (
                  <button
                    key={`${action.label}-${action.href || action.tab}`}
                    type="button"
                    onClick={() => onAction?.(action)}
                  >
                    {action.label}
                  </button>
                ))}
              </div>
            ) : null}
          </div>
          <div className="exec-insight-card__metric">
            <span>Supporting metric</span>
            <strong>{item.supportingMetric}</strong>
          </div>
        </>
      ) : null}
    </article>
  );
}

function shortenDestination(label) {
  if (!label) return label;
  // Truncate after 40 chars for display; full label kept in title attribute
  if (label.length <= 40) return label;
  return `${label.slice(0, 38).trimEnd()}…`;
}

function MetricCard({ label, value, detail, tone = "default" }) {
  return (
    <article className={`analytics-metric analytics-metric--${tone}`}>
      <span>{label}</span>
      <strong>{value}</strong>
      {detail ? <small>{detail}</small> : null}
    </article>
  );
}

function EmptyState({ children = "No analytics data available for this period." }) {
  return <div className="analytics-empty-state">{children}</div>;
}

function SectionHead({ title, description, meta }) {
  return (
    <div className="analytics-section-head">
      <div>
        <h2>{title}</h2>
        {description ? <p>{description}</p> : null}
      </div>
      {meta ? <span>{meta}</span> : null}
    </div>
  );
}

function DataBar({ label, value, max, meta, valueLabel, fullLabel }) {
  const width = Number(value || 0) > 0 ? Math.max(4, Math.round((Number(value || 0) / max) * 100)) : 0;
  const displayLabel = fullLabel ? shortenDestination(label) : label;

  return (
    <div className="analytics-bar-row">
      <div className="analytics-bar-row__head">
        <strong title={fullLabel ? label : undefined} className={fullLabel ? "analytics-bar-row__label--truncate" : undefined}>
          {displayLabel}
        </strong>
        <span>{valueLabel || formatNumber(value)}</span>
      </div>
      <div className="analytics-bar-row__track">
        <span style={{ width: `${width}%` }} />
      </div>
      {meta ? <small>{meta}</small> : null}
    </div>
  );
}

function parseChartDate(item = {}) {
  const raw = item.key || item.date || item.label;
  const parsed = raw ? new Date(raw) : null;
  return parsed && !Number.isNaN(parsed.getTime()) ? parsed : null;
}

function formatMonthOnlyLabel(value) {
  const text = String(value || "").trim();
  if (/^\d{4}-\d{2}$/.test(text)) {
    const [year, month] = text.split("-").map(Number);
    return new Date(year, month - 1, 1).toLocaleString("en-US", { month: "short" });
  }

  const parsed = new Date(`${text} 1, 2026`);
  if (/^[A-Za-z]{3,9}$/.test(text) && !Number.isNaN(parsed.getTime())) {
    return parsed.toLocaleString("en-US", { month: "short" });
  }

  return "";
}

function formatChartTick(item = {}, totalRows = 0) {
  const monthOnlyLabel = formatMonthOnlyLabel(item.key) || formatMonthOnlyLabel(item.label);
  if (monthOnlyLabel) return monthOnlyLabel;

  const parsed = parseChartDate(item);
  if (!parsed) return item.label || item.key || "";

  if (totalRows > 45) {
    return parsed.toLocaleString("en-US", { month: "short" });
  }

  return parsed.toLocaleString("en-US", { month: "short", day: "numeric" });
}

function formatChartTooltipDate(item = {}) {
  const monthOnlyLabel = formatMonthOnlyLabel(item.key) || formatMonthOnlyLabel(item.label);
  if (monthOnlyLabel && /^\d{4}-\d{2}$/.test(String(item.key || ""))) {
    return `${monthOnlyLabel} ${String(item.key).slice(0, 4)}`;
  }
  if (monthOnlyLabel && /^[A-Za-z]{3,9}$/.test(String(item.label || item.key || "").trim())) {
    return monthOnlyLabel;
  }

  const parsed = parseChartDate(item);
  if (!parsed) return item.label || item.key || "Selected date";
  return parsed.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

function getLineChartTickIndexes(rows = []) {
  const total = rows.length;
  if (total <= 7) {
    return new Set(rows.map((_, index) => index));
  }

  const targetTicks = total <= 14 ? Math.min(7, total) : 7;
  const indexes = new Set();
  for (let i = 0; i < targetTicks; i++) {
    indexes.add(Math.round((i * (total - 1)) / (targetTicks - 1)));
  }
  indexes.add(0);
  indexes.add(total - 1);
  return indexes;
}

function LineChart({ data, valueKey, secondaryKey, valueFormatter = formatNumber }) {
  const rows = toArray(data);
  if (!rows.length) {
    return <EmptyState>No chart data available for this period.</EmptyState>;
  }

  const tickIndexes = getLineChartTickIndexes(rows);
  const isSparse = rows.length <= 1;
  const values = rows.map((item) => Number(item?.[valueKey] || 0));
  const secondaryValues = secondaryKey ? rows.map((item) => Number(item?.[secondaryKey] || 0)) : [];
  const max = Math.max(...values, ...secondaryValues, 1);
  const width = 640;
  const height = 220;
  const left = 46;
  const right = 18;
  const top = 16;
  const bottom = 50;
  const chartWidth = width - left - right;
  const chartHeight = height - top - bottom;

  const pointFor = (item, index, key) => {
    const x = rows.length === 1 ? left + chartWidth / 2 : left + (index / (rows.length - 1)) * chartWidth;
    const y = top + chartHeight - (Number(item?.[key] || 0) / max) * chartHeight;
    return `${x},${y}`;
  };

  return (
    <div className="analytics-chart analytics-line-chart">
      <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Trend chart">
        <line x1={left} y1={top + chartHeight} x2={width - right} y2={top + chartHeight} />
        <line x1={left} y1={top} x2={left} y2={top + chartHeight} />
        <text x={left - 8} y={top + 8} textAnchor="end">{valueFormatter(max)}</text>
        <text x={left - 8} y={top + chartHeight} textAnchor="end">0</text>
        {secondaryKey ? (
          <polyline
            className="analytics-chart__line analytics-chart__line--muted"
            points={rows.map((item, index) => pointFor(item, index, secondaryKey)).join(" ")}
          />
        ) : null}
        <polyline
          className="analytics-chart__line"
          points={rows.map((item, index) => pointFor(item, index, valueKey)).join(" ")}
        />
        {rows.map((item, index) => {
          const [x, y] = pointFor(item, index, valueKey).split(",");
          const showLabel = tickIndexes.has(index);
          const showDot = rows.length <= 31;
          const primaryValue = Number(item?.[valueKey] || 0);
          const secondaryValue = secondaryKey ? Number(item?.[secondaryKey] || 0) : null;
          return (
            <g key={`${item.key || item.label}-${index}`}>
              <title>
                {`${formatChartTooltipDate(item)}
${valueKey === "total" ? "Bookings" : valueKey}: ${valueFormatter(primaryValue)}${
                  secondaryKey ? `\n${secondaryKey}: ${valueFormatter(secondaryValue)}` : ""
                }`}
              </title>
              {showDot ? <circle cx={x} cy={y} r="4" /> : null}
              {showLabel ? <text x={x} y={height - 16} textAnchor="middle">{formatChartTick(item, rows.length)}</text> : null}
            </g>
          );
        })}
      </svg>
      {isSparse ? (
        <p className="analytics-chart-hint">More trend data will appear as bookings accumulate over time.</p>
      ) : null}
    </div>
  );
}

function VerticalBarChart({ data, valueKey, valueFormatter = formatNumber }) {
  const rows = toArray(data);
  const max = getMax(rows, valueKey);
  if (!rows.length || rows.every((item) => Number(item?.[valueKey] || 0) <= 0)) {
    return <EmptyState>No chart data available for this period.</EmptyState>;
  }

  return (
    <div className="analytics-column-chart">
      {rows.map((item, index) => {
        const value = Number(item?.[valueKey] || 0);
        const height = Math.max(4, Math.round((value / max) * 100));
        return (
          <div className="analytics-column" key={`${item.label || item.category || index}`}>
            <div className="analytics-column__bar">
              <span style={{ height: `${height}%` }} />
            </div>
            <strong>{valueFormatter(value)}</strong>
            <small>{item.label || item.category || item.vehicleName || item.destination}</small>
          </div>
        );
      })}
    </div>
  );
}

function DonutChart({ items }) {
  const rows = toArray(items).filter((item) => Number(item.value || 0) > 0);
  const total = rows.reduce((sum, item) => sum + Number(item.value || 0), 0);
  if (!rows.length || total <= 0) {
    return <EmptyState>No chart data available for this period.</EmptyState>;
  }

  let offset = 0;
  const colors = ["#f97316", "#2563eb", "#16a34a", "#eab308", "#dc2626", "#7c3aed", "#0891b2"];
  const gradient = rows
    .map((item, index) => {
      const start = offset;
      offset += (Number(item.value || 0) / total) * 100;
      return `${colors[index % colors.length]} ${start}% ${offset}%`;
    })
    .join(", ");

  return (
    <div className="analytics-donut-wrap">
      <div className="analytics-donut" style={{ background: `conic-gradient(${gradient})` }}>
        <span>{formatNumber(total)}</span>
      </div>
      <div className="analytics-donut-legend">
        {rows.map((item, index) => (
          <div key={item.label}>
            <i style={{ background: colors[index % colors.length] }} />
            <span>{item.label}</span>
            <strong>{formatNumber(item.value)}</strong>
          </div>
        ))}
      </div>
    </div>
  );
}

function QueueCard({ item, onOpen }) {
  return (
    <article className="analytics-queue-card">
      <div>
        <span>{item.label}</span>
        <strong>{formatNumber(item.count)}</strong>
        <p>{item.description}</p>
      </div>
      {item.href ? (
        <button type="button" onClick={() => onOpen(item)}>
          {item.actionLabel || "Open"}
        </button>
      ) : null}
    </article>
  );
}

/* ── Executive Area Chart ─────────────────────────────────────────────────── */
function ExecAreaChart({ data, valueKey, valueFormatter = formatCurrency, label = "Revenue" }) {
  const rows = toArray(data);
  if (!rows.length) {
    return (
      <div className="exec-chart-empty">
        <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M3 17l4-7 4 4 4-6 4 6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <span>Revenue trend will appear once revenue data is available for this period.</span>
      </div>
    );
  }

  const W = 640;
  const H = 200;
  const padL = 56;
  const padR = 16;
  const padT = 16;
  const padB = 36;
  const cW = W - padL - padR;
  const cH = H - padT - padB;

  const values = rows.map((r) => Number(r?.[valueKey] || 0));
  const maxVal = Math.max(...values, 1);
  const ySteps = 4;

  const xOf = (i) => padL + (rows.length === 1 ? cW / 2 : (i / (rows.length - 1)) * cW);
  const yOf = (v) => padT + cH - (v / maxVal) * cH;

  // Smooth bezier path
  const linePts = rows.map((r, i) => [xOf(i), yOf(Number(r?.[valueKey] || 0))]);
  const pathD = linePts.reduce((acc, [x, y], i) => {
    if (i === 0) return `M ${x} ${y}`;
    const [px, py] = linePts[i - 1];
    const cpx = (px + x) / 2;
    return `${acc} C ${cpx} ${py} ${cpx} ${y} ${x} ${y}`;
  }, "");
  const areaD = `${pathD} L ${linePts[linePts.length - 1][0]} ${padT + cH} L ${linePts[0][0]} ${padT + cH} Z`;

  return (
    <div className="exec-area-chart">
      <svg viewBox={`0 0 ${W} ${H}`} aria-label={`${label} trend chart`} role="img">
        <defs>
          <linearGradient id="execAreaGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#f97316" stopOpacity="0.18" />
            <stop offset="100%" stopColor="#f97316" stopOpacity="0.01" />
          </linearGradient>
        </defs>
        {/* Y-axis grid lines + labels */}
        {Array.from({ length: ySteps + 1 }, (_, i) => {
          const ratio = i / ySteps;
          const yPos = padT + cH - ratio * cH;
          const val = maxVal * ratio;
          return (
            <g key={`ygrid-${i}`}>
              <line
                x1={padL} y1={yPos} x2={W - padR} y2={yPos}
                stroke={i === 0 ? "#cbd5e1" : "#f1f5f9"}
                strokeWidth={i === 0 ? 1.5 : 1}
                strokeDasharray={i === 0 ? "0" : "4 4"}
              />
              <text x={padL - 6} y={yPos + 5} textAnchor="end" className="exec-chart-ylabel">
                {valueFormatter(val)}
              </text>
            </g>
          );
        })}
        {/* Area fill */}
        <path d={areaD} fill="url(#execAreaGrad)" />
        {/* Line */}
        <path d={pathD} fill="none" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        {/* X-axis labels + dots */}
        {rows.map((r, i) => {
          const x = xOf(i);
          const y = yOf(Number(r?.[valueKey] || 0));
          const showLabel = rows.length <= 12 || i % Math.ceil(rows.length / 12) === 0 || i === rows.length - 1;
          return (
            <g key={`pt-${i}`}>
              <circle cx={x} cy={y} r="4" fill="#f97316" stroke="#ffffff" strokeWidth="2.5" />
              {showLabel ? (
                <text x={x} y={H - 6} textAnchor="middle" className="exec-chart-xlabel">
                  {r.label || ""}
                </text>
              ) : null}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

/* ── Executive Fleet Health Bars ─────────────────────────────────────────── */
function ExecFleetBar({ label, count, total, tone = "default" }) {
  const pct = total > 0 ? Math.min(Math.round((count / total) * 100), 100) : 0;
  return (
    <div className="exec-fleet-bar">
      <div className="exec-fleet-bar__head">
        <span>{label}</span>
        <strong>{count} <small>/ {total}</small></strong>
      </div>
      <div className="exec-fleet-bar__track">
        <div className={`exec-fleet-bar__fill exec-fleet-bar__fill--${tone}`} style={{ width: `${pct}%` }} />
      </div>
      <div className="exec-fleet-bar__pct">{pct}%</div>
    </div>
  );
}

export default function AnalyticsReports() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState("business");
  const [range, setRange] = useState("this_month");
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [exportingReport, setExportingReport] = useState("");
  const [exportingPdfReport, setExportingPdfReport] = useState("");
  const [exportError, setExportError] = useState("");
  const [exportNotice, setExportNotice] = useState("");
  const canExportReports = user?.role === "technical_admin" || user?.role === "admin";

  const loadAnalytics = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const params = { range };
      if (range === "custom") {
        params.startDate = customStartDate;
        params.endDate = customEndDate;
      }

      const res = await api.get("/admin/analytics/summary", { params });
      setAnalytics(res.data || null);
    } catch (err) {
      setError(err?.response?.data?.message || "Unable to load analytics. Please try again.");
      setAnalytics(null);
    } finally {
      setLoading(false);
    }
  }, [customEndDate, customStartDate, range]);

  useEffect(() => {
    if (range === "custom" && (!customStartDate || !customEndDate)) {
      setAnalytics(null);
      setError("");
      setLoading(false);
      return;
    }

    loadAnalytics();
  }, [customEndDate, customStartDate, loadAnalytics, range]);

  useEffect(() => {
    const incomingTab = location.state?.activeTab;
    if (incomingTab && TABS.some((tab) => tab.key === incomingTab)) {
      setActiveTab(incomingTab);
    }
  }, [location.state?.activeTab]);

  const overview = analytics?.overview || analytics?.metrics || {};
  const fleet = analytics?.fleet || {};
  const rangeStartIso = analytics?.range?.startDate || null;
  const rangeEndIso = analytics?.range?.endDate || null;
  const rangePreset = analytics?.range?.preset || "";
  const shouldFillDays = rangePreset !== "this_year";
  const bookingTrends = (() => {
    const raw = toArray(analytics?.bookingTrends || analytics?.trend).map((item) => ({
      ...item,
      total: item.total ?? item.bookings ?? 0,
    }));
    return shouldFillDays ? fillTrendGaps(raw, rangeStartIso, rangeEndIso, ["total", "confirmed", "completed", "cancelled"]) : raw;
  })();
  const revenueTrends = (() => {
    const raw = toArray(analytics?.revenueTrends);
    return shouldFillDays ? fillTrendGaps(raw, rangeStartIso, rangeEndIso, ["revenue", "paidBookings"]) : raw;
  })();
  const categoryDemand = toArray(analytics?.categoryDemand || fleet.categoryDemand);
  const vehicleUtilization = toArray(analytics?.vehicleUtilization || fleet.vehicleUtilization);
  const mostBookedVehicles = toArray(fleet.mostBookedVehicles);
  const underutilizedVehicles = toArray(fleet.underutilizedVehicles || fleet.leastBookedVehicles);
  const highUtilizationVehicles = toArray(fleet.highUtilizationVehicles);
  const customerVerification = analytics?.customerVerification || {};
  const customerInsights = analytics?.customerInsights || {};
  const routeInsights = analytics?.routeInsights || {};
  const operationsQueue = analytics?.operationsQueue || {};
  const operational = analytics?.operational || {};
  const reports = useMemo(() => analytics?.reportsSummary || analytics?.reports || {}, [analytics]);
  const rangeLabel = getRangeLabel(analytics?.range);
  const hasData = Number(overview.totalBookings || 0) > 0;
  const exportDisabledForRange = range === "custom" && (!customStartDate || !customEndDate);
  const recommendations = useMemo(() => {
    return toArray(analytics?.recommendations)
      .map(buildRecommendationView)
      .sort(compareRecommendations);
  }, [analytics?.recommendations]);
  const recommendationCounts = useMemo(() => {
    return recommendations.reduce(
      (counts, item) => ({
        ...counts,
        [item.priorityKey]: Number(counts[item.priorityKey] || 0) + 1,
      }),
      { high: 0, medium: 0, low: 0 }
    );
  }, [recommendations]);

  const statusItems = Object.entries(analytics?.bookingStatusDistribution || {}).map(([key, value]) => ({
    label: STATUS_LABELS[key] || key,
    value,
  }));
  const verificationItems = [
    { label: "Verified", value: customerVerification.verifiedCustomers ?? customerVerification.fullyVerified ?? customerInsights.verifiedCustomers },
    { label: "Pending", value: customerVerification.pending ?? customerInsights.pendingVerification },
    { label: "Rejected", value: customerVerification.rejected ?? customerInsights.rejectedVerification },
    { label: "Expiring Soon", value: customerVerification.expiringSoon ?? customerInsights.expiringSoon },
    { label: "Expired", value: customerVerification.expired ?? customerInsights.expired },
  ];
  const queueItems = Object.values(operationsQueue).length
    ? Object.values(operationsQueue)
    : [
        { label: "Payment Verification", count: operational.paymentVerificationQueue, description: "Bookings with submitted payments waiting for review.", href: "/bookings?status=payment_verification" },
        { label: "Extension Requests", count: operational.extensionRequestsPending, description: "Rental extension requests pending admin decision.", href: "/bookings?status=extension_requests" },
        { label: "Additional Payments", count: operational.additionalPaymentsPending, description: "Additional payment proofs waiting for review.", href: "/bookings?status=additional_payments" },
        { label: "Active Rentals", count: operational.activeRentals, description: "Rentals currently active or ongoing.", href: "/bookings?status=active" },
      ];

  const reportCards = useMemo(() => {
    return [
      {
        id: "bookings",
        name: "Booking Summary Report",
        description: "Bookings by status for the selected period.",
        fileSlug: "booking-summary",
        noData: Number(reports.bookingSummary?.totalBookings || 0) <= 0,
        numbers: [
          ["Total", reports.bookingSummary?.totalBookings],
          ["Confirmed", reports.bookingSummary?.confirmedBookings],
          ["Completed", reports.bookingSummary?.completedBookings],
          ["Cancelled", reports.bookingSummary?.cancelledBookings],
        ],
      },
      {
        id: "revenue",
        name: "Revenue Summary Report",
        description: "Collected revenue from verified/paid bookings only.",
        fileSlug: "revenue-summary",
        noData: Number(reports.revenueSummary?.revenueCollected || 0) <= 0,
        numbers: [
          ["Revenue", formatCurrency(reports.revenueSummary?.revenueCollected)],
          ["Average", formatCurrency(reports.revenueSummary?.averageBookingValue)],
          ["Pending payments", reports.revenueSummary?.pendingPayments],
        ],
      },
      {
        id: "vehicles",
        name: "Vehicle Utilization Report",
        description: "Vehicle availability, utilization rates, and top booked unit summary.",
        fileSlug: "vehicle-utilization",
        noData: Number(reports.vehicleUtilization?.totalVehicles || 0) <= 0,
        numbers: [
          ["Total vehicles", reports.vehicleUtilization?.totalVehicles],
          ["Available", reports.vehicleUtilization?.availableVehicles],
          ["Top unit", reports.vehicleUtilization?.topVehicle?.vehicleName || "N/A"],
        ],
      },
      {
        id: "verifications",
        name: "Customer Verification Report",
        description: "Customer growth, verification, and expiry snapshot.",
        fileSlug: "customer-verification",
        noData:
          Number(reports.customerVerification?.newCustomers || 0) <= 0 &&
          Number(reports.customerVerification?.verifiedCustomers || 0) <= 0,
        numbers: [
          ["New customers", reports.customerVerification?.newCustomers],
          ["Verified customers", reports.customerVerification?.verifiedCustomers],
          ["Expiring soon", reports.customerVerification?.expiringSoon],
          ["Repeat customers", reports.customerVerification?.repeatCustomers],
        ],
      },
      {
        id: "payments",
        name: "Payment Verification Report",
        description: "Payment verification queue and reviewed payment activity.",
        fileSlug: "payment-verification",
        noData:
          Number(reports.paymentVerification?.paymentVerificationQueue || 0) <= 0 &&
          Number(reports.revenueSummary?.pendingPayments || 0) <= 0,
        numbers: [
          ["Verification queue", reports.paymentVerification?.paymentVerificationQueue],
          ["Pending payments", reports.revenueSummary?.pendingPayments],
        ],
      },
      {
        id: "extensions-penalties",
        name: "Extension & Penalty Report",
        description: "Extension requests, return assessment, penalties, and additional payments.",
        fileSlug: "extension-penalty",
        noData:
          Number(reports.penaltyAdditionalPayment?.additionalPaymentsPending || 0) <= 0 &&
          Number(reports.penaltyAdditionalPayment?.penaltyOrAdditionalRequired || 0) <= 0,
        numbers: [
          ["Pending additional", reports.penaltyAdditionalPayment?.additionalPaymentsPending],
          ["Required payments", reports.penaltyAdditionalPayment?.penaltyOrAdditionalRequired],
        ],
      },
    ];
  }, [reports]);

  const handleExportReport = async (report) => {
    if (!canExportReports || exportDisabledForRange) return;

    try {
      setExportError("");
      setExportNotice("");
      setExportingReport(report.id);
      const params = { range };
      if (range === "custom") {
        params.startDate = customStartDate;
        params.endDate = customEndDate;
      }

      const res = await api.get(`/admin/reports/${report.id}`, {
        params,
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const today = new Date().toISOString().slice(0, 10);
      link.href = url;
      link.download = `fleetx-${report.fileSlug}-${today}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setExportNotice(`${report.name} CSV prepared successfully.`);
    } catch (err) {
      setExportError(err?.response?.data?.message || "Unable to generate report. Please try again.");
    } finally {
      setExportingReport("");
    }
  };

  const handleExportPdf = async (report) => {
    if (!canExportReports || exportDisabledForRange) return;

    try {
      setExportError("");
      setExportNotice("");
      setExportingPdfReport(report.id);
      const params = { range };
      if (range === "custom") {
        params.startDate = customStartDate;
        params.endDate = customEndDate;
      }

      const res = await api.get(`/admin/reports/${report.id}/pdf`, {
        params,
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const startStr = customStartDate || new Date().toISOString().slice(0, 10);
      const endStr = customEndDate || new Date().toISOString().slice(0, 10);
      const datePart = range === "custom" ? `${startStr}-to-${endStr}` : new Date().toISOString().slice(0, 10);
      link.download = `FleetX-${report.fileSlug}-${datePart}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setExportNotice(`${report.name} PDF prepared successfully.`);
    } catch (err) {
      setExportError(
        err?.response?.data?.message || "PDF report could not be generated. Please try again."
      );
    } finally {
      setExportingPdfReport("");
    }
  };

  const openQueueTarget = (item) => {
    const key = item?.key || "";
    if (key === "payment_verification") {
      navigate("/bookings", { state: { statusFilter: "payment_verification" } });
      return;
    }
    if (key === "extension_requests") {
      navigate("/bookings", { state: { statusFilter: "extension_requests" } });
      return;
    }
    if (key === "additional_payments") {
      navigate("/bookings", { state: { statusFilter: "additional_payments" } });
      return;
    }
    if (key === "active_rentals") {
      navigate("/bookings", { state: { statusFilter: "active" } });
      return;
    }
    if (key === "upcoming_rentals") {
      navigate("/bookings", { state: { statusFilter: "confirmed", quickFilter: "upcoming" } });
      return;
    }
    if (key === "route_review") {
      navigate("/bookings", { state: { statusFilter: "all", quickFilter: "route_review" } });
      return;
    }
    if (key === "expiring_verifications") {
      navigate("/users");
      return;
    }
    navigate(item?.href || "/bookings");
  };

  const openRecommendationAction = (action) => {
    if (action?.tab) {
      setActiveTab(action.tab);
      navigate("/analytics-reports", { state: { activeTab: action.tab } });
      return;
    }
    navigate(action?.href || "/bookings");
  };

  const analyticsLoadingTitle = exportingPdfReport
    ? "Preparing report..."
    : exportingReport
    ? "Exporting file..."
    : "";

  return (
    <AdminShell
      pageClassName="admin-shell--analytics"
      eyebrow="BUSINESS INSIGHTS"
      title="Analytics & Reports"
      subtitle="Track booking performance, revenue trends, fleet utilization, route demand, and operational queues."
      hideTopbarCopy
    >
      <div className="analytics-page">
        <AdminLoadingOverlay
          open={Boolean(analyticsLoadingTitle)}
          title={analyticsLoadingTitle}
          message="Please wait while FleetX prepares the report file."
        />
        <section className="analytics-toolbar">
          <div className="analytics-tabs" role="tablist" aria-label="Analytics sections">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                type="button"
                className={activeTab === tab.key ? "is-active" : ""}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="analytics-filters">
            <label>
              Period
              <select value={range} onChange={(event) => setRange(event.target.value)}>
                {RANGE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            {range === "custom" ? (
              <>
                <label>
                  From
                  <input
                    type="date"
                    value={customStartDate}
                    onChange={(event) => setCustomStartDate(event.target.value)}
                  />
                </label>
                <label>
                  To
                  <input
                    type="date"
                    value={customEndDate}
                    onChange={(event) => setCustomEndDate(event.target.value)}
                  />
                </label>
              </>
            ) : null}
          </div>
        </section>

        {loading ? <div className="analytics-state">Loading analytics...</div> : null}
        {!loading && error ? <div className="analytics-state analytics-state--error">{error}</div> : null}
        {!loading && !error && !analytics && range === "custom" ? (
          <div className="analytics-state">Select a custom date range to load analytics.</div>
        ) : null}

        {!loading && !error && analytics ? (
          <>
            <div className="analytics-range-note">
              <strong>{analytics.range?.label || "Selected Period"}</strong>
              <span>{rangeLabel}</span>
            </div>

            {activeTab === "business" ? (
              <section className="analytics-section exec-overview">

                {/* ── SECTION 1: Executive Performance Header ───────────────────── */}
                <div className="exec-hero">
                  <div className="exec-hero__chart">
                    <div className="exec-hero__chart-head">
                      <span className="exec-eyebrow">Revenue Trend</span>
                      <span className="exec-hero__chart-meta">{rangeLabel}</span>
                    </div>
                    <ExecAreaChart data={revenueTrends} valueKey="revenue" valueFormatter={formatCurrency} label="Revenue" />
                  </div>
                  <div className="exec-kpi-stack">
                    <div className="exec-kpi exec-kpi--primary">
                      <span className="exec-kpi__label">Revenue Collected</span>
                      <strong className="exec-kpi__value">{formatCurrency(overview.revenueCollected)}</strong>
                    </div>
                    <div className="exec-kpi">
                      <span className="exec-kpi__label">Total Bookings</span>
                      <strong className="exec-kpi__value">{formatNumber(overview.totalBookings)}</strong>
                    </div>
                    <div className="exec-kpi">
                      <span className="exec-kpi__label">Avg Booking Value</span>
                      <strong className="exec-kpi__value">{formatCurrency(overview.averageBookingValue)}</strong>
                    </div>
                    <div className="exec-kpi">
                      <span className="exec-kpi__label">Active Rentals</span>
                      <strong className="exec-kpi__value">{formatNumber(overview.activeRentals)}</strong>
                    </div>
                  </div>
                </div>

                {/* ── SECTION 2: Business Health Snapshot ───────────────────────── */}
                <div className="exec-section-label">Business Health</div>
                <div className="exec-health-grid">
                  <section className="exec-health-card">
                    <header className="exec-health-card__head">
                      <h3>Booking Status Distribution</h3>
                      <span>{rangeLabel}</span>
                    </header>
                    <DonutChart items={statusItems} />
                  </section>

                  <section className="exec-health-card">
                    <header className="exec-health-card__head">
                      <h3>Fleet Utilization</h3>
                      <span>Current snapshot</span>
                    </header>
                    <div className="exec-fleet-bars">
                      <ExecFleetBar
                        label="Available"
                        count={Number(fleet.availableVehicles || 0)}
                        total={Number(fleet.totalVehicles || 0)}
                        tone="success"
                      />
                      <ExecFleetBar
                        label="On Rental"
                        count={Number(fleet.onRentalVehicles || fleet.activeVehicles || 0)}
                        total={Number(fleet.totalVehicles || 0)}
                        tone="warning"
                      />
                      <ExecFleetBar
                        label="Maintenance"
                        count={Number(fleet.maintenanceVehicles || 0)}
                        total={Number(fleet.totalVehicles || 0)}
                        tone="neutral"
                      />
                      <div className="exec-fleet-total">
                        <span>Total Fleet</span>
                        <strong>{formatNumber(fleet.totalVehicles)}</strong>
                      </div>
                    </div>
                  </section>

                  <section className="exec-health-card">
                    <header className="exec-health-card__head">
                      <h3>Operational Workload</h3>
                      <span>Pending review items</span>
                    </header>
                    <div className="exec-workload-list">
                      {queueItems.slice(0, 4).map((item) => (
                        <div key={item.key || item.label} className="exec-workload-row">
                          <div className="exec-workload-row__body">
                            <strong>{item.label}</strong>
                          </div>
                          <div className="exec-workload-row__right">
                            <span className="exec-workload-row__count">{formatNumber(item.count)}</span>
                            {item.href || item.key ? (
                              <button
                                type="button"
                                className="exec-workload-row__open"
                                onClick={() => openQueueTarget(item)}
                              >
                                Open
                              </button>
                            ) : null}
                          </div>
                        </div>
                      ))}
                    </div>
                  </section>
                </div>

                {/* ── SECTION 3: Executive Insights ────────────────────────────── */}
                {recommendations.length ? (
                  <>
                    <div className="exec-section-label">Executive Insights</div>
                    <div className="exec-insights-grid">
                      {recommendations.slice(0, 3).map((item, index) => (
                        <RecommendationCard
                          key={`${item.type || item.category || "recommendation"}-${item.title}-${index}`}
                          item={item}
                          compact
                          onAction={openRecommendationAction}
                        />
                      ))}
                    </div>
                  </>
                ) : null}

                {/* ── SECTION 4: Reports Quick Access ──────────────────────────── */}
                <div className="exec-section-label">Reports Quick Access</div>
                <div className="exec-reports-grid">
                  {reportCards.slice(0, 3).map((report) => (
                    <article key={report.id} className="exec-report-card">
                      <div className="exec-report-card__body">
                        <h4>{report.name}</h4>
                        <p>{report.description}</p>
                        {report.numbers.slice(0, 2).map(([label, value]) => (
                          <div key={label} className="exec-report-card__stat">
                            <span>{label}</span>
                            <strong>{value ?? 0}</strong>
                          </div>
                        ))}
                      </div>
                      <div className="exec-report-card__actions">
                        <button
                          type="button"
                          className="exec-report-btn exec-report-btn--pdf"
                          onClick={() => handleExportPdf(report)}
                          disabled={!canExportReports || exportDisabledForRange || exportingPdfReport === report.id}
                        >
                          {exportingPdfReport === report.id ? "Generating…" : "Download PDF"}
                        </button>
                        <button
                          type="button"
                          className="exec-report-btn exec-report-btn--csv"
                          onClick={() => handleExportReport(report)}
                          disabled={!canExportReports || exportDisabledForRange || exportingReport === report.id}
                        >
                          {exportingReport === report.id ? "Exporting…" : "CSV"}
                        </button>
                      </div>
                    </article>
                  ))}
                </div>

              </section>
            ) : null}

            {activeTab === "bookings" ? (
              <section className="analytics-section exec-tab">

                <div className="exec-tab-header">
                  <div>
                    <span className="exec-eyebrow">Booking Performance</span>
                    <h2>Bookings</h2>
                  </div>
                  <span className="exec-tab-header__meta">{rangeLabel}</span>
                </div>

                <div className="exec-kpi-row">
                  {[
                    { label: "Total Bookings", value: formatNumber(overview.totalBookings) },
                    { label: "Confirmed", value: formatNumber(overview.confirmedBookings) },
                    { label: "Completed", value: formatNumber(overview.completedBookings) },
                    { label: "Cancelled", value: formatNumber(overview.cancelledBookings), alert: Number(overview.cancelledBookings) > 0 },
                    { label: "Needs Attention", value: formatNumber(overview.pendingPayments), alert: Number(overview.pendingPayments) > 0 },
                  ].map((kpi) => (
                    <div key={kpi.label} className={`exec-kpi-row__item${kpi.alert ? " exec-kpi-row__item--alert" : ""}`}>
                      <span>{kpi.label}</span>
                      <strong>{kpi.value}</strong>
                    </div>
                  ))}
                </div>

                <div className="exec-section-label">Booking Activity</div>
                <div className="exec-two-col">
                  <div className="exec-panel">
                    <div className="exec-panel__head">
                      <h3>Booking Trend</h3>
                      <span>Volume over period</span>
                    </div>
                    <div className="exec-panel__body">
                      <LineChart data={bookingTrends} valueKey="total" secondaryKey="confirmed" />
                    </div>
                  </div>
                  <div className="exec-panel">
                    <div className="exec-panel__head">
                      <h3>Status Distribution</h3>
                      <span>Booking pipeline breakdown</span>
                    </div>
                    <div className="exec-panel__body">
                      <DonutChart items={statusItems} />
                    </div>
                  </div>
                </div>

              </section>
            ) : null}

            {activeTab === "revenue" ? (
              <section className="analytics-section exec-tab">

                <div className="exec-tab-header">
                  <div>
                    <span className="exec-eyebrow">Revenue Performance</span>
                    <h2>Revenue</h2>
                  </div>
                  <span className="exec-tab-header__meta">{rangeLabel}</span>
                </div>

                <div className="exec-hero">
                  <div className="exec-hero__chart">
                    <div className="exec-hero__chart-head">
                      <span className="exec-eyebrow">Revenue Trend</span>
                      <span className="exec-hero__chart-meta">{rangeLabel}</span>
                    </div>
                    <ExecAreaChart data={revenueTrends} valueKey="revenue" valueFormatter={formatCurrency} label="Revenue" />
                  </div>
                  <div className="exec-kpi-stack">
                    <div className="exec-kpi exec-kpi--primary">
                      <span className="exec-kpi__label">Revenue Collected</span>
                      <strong className="exec-kpi__value">{formatCurrency(overview.revenueCollected)}</strong>
                    </div>
                    <div className="exec-kpi">
                      <span className="exec-kpi__label">Avg Booking Value</span>
                      <strong className="exec-kpi__value">{formatCurrency(overview.averageBookingValue)}</strong>
                    </div>
                    <div className="exec-kpi">
                      <span className="exec-kpi__label">Pending Payments</span>
                      <strong className="exec-kpi__value">{formatNumber(overview.pendingPayments)}</strong>
                    </div>
                    <div className="exec-kpi">
                      <span className="exec-kpi__label">Additional Pending</span>
                      <strong className="exec-kpi__value">{formatNumber(overview.additionalPaymentsPending)}</strong>
                    </div>
                  </div>
                </div>

                <div className="exec-section-label">Revenue Breakdown</div>
                <div className="exec-panel">
                  <div className="exec-panel__head">
                    <h3>Revenue by Vehicle Category</h3>
                    <span>Verified / paid bookings only</span>
                  </div>
                  <div className="exec-panel__body">
                    {categoryDemand.length ? (
                      <div className="analytics-bar-list">
                        {categoryDemand.map((item) => (
                          <DataBar
                            key={item.category}
                            label={item.category}
                            value={item.revenue}
                            valueLabel={formatCurrency(item.revenue)}
                            max={getMax(categoryDemand, "revenue")}
                            meta={`${formatNumber(item.bookings)} bookings`}
                          />
                        ))}
                      </div>
                    ) : (
                      <EmptyState>Revenue by category will appear once more verified bookings are recorded.</EmptyState>
                    )}
                  </div>
                </div>
                <p className="exec-footnote">Revenue figures reflect verified or paid booking amounts only.</p>

              </section>
            ) : null}

            {activeTab === "fleet" ? (
              <section className="analytics-section exec-tab">

                <div className="exec-tab-header">
                  <div>
                    <span className="exec-eyebrow">Vehicle Utilization Overview</span>
                    <h2>Vehicle Utilization</h2>
                  </div>
                  <span className="exec-tab-header__meta">{rangeLabel}</span>
                </div>

                <div className="exec-hero">
                  <div className="exec-hero__chart">
                    <div className="exec-hero__chart-head">
                      <span className="exec-eyebrow">Utilization by Unit</span>
                      <span className="exec-hero__chart-meta">Booked days / available days</span>
                    </div>
                    <div className="exec-panel__body">
                      {vehicleUtilization.length ? (
                        <div className="analytics-bar-list analytics-bar-list--dense">
                          {vehicleUtilization.slice(0, 8).map((item) => (
                            <DataBar
                              key={item.vehicleId}
                              label={item.vehicleName}
                              value={item.utilizationRate}
                              valueLabel={formatPercent(item.utilizationRate)}
                              max={100}
                              meta={`${item.category} · ${formatNumber(item.bookedDays)} booked / ${formatNumber(item.availableDays)} available`}
                            />
                          ))}
                        </div>
                      ) : (
                        <EmptyState>Utilization data will appear once booking records accumulate.</EmptyState>
                      )}
                    </div>
                  </div>
                  <div className="exec-kpi-stack">
                    <div className="exec-kpi exec-kpi--primary">
                      <span className="exec-kpi__label">Total Vehicles</span>
                      <strong className="exec-kpi__value">{formatNumber(fleet.totalVehicles)}</strong>
                    </div>
                    <div className="exec-kpi">
                      <span className="exec-kpi__label">Available Vehicles</span>
                      <strong className="exec-kpi__value">{formatNumber(fleet.availableVehicles)}</strong>
                    </div>
                    <div className="exec-kpi">
                      <span className="exec-kpi__label">Rented / Ongoing</span>
                      <strong className="exec-kpi__value">{formatNumber(fleet.onRentalVehicles || fleet.activeVehicles)}</strong>
                    </div>
                    <div className="exec-kpi">
                      <span className="exec-kpi__label">Maintenance Vehicles</span>
                      <strong className="exec-kpi__value">{formatNumber(fleet.maintenanceVehicles)}</strong>
                    </div>
                  </div>
                </div>

                <div className="exec-section-label">Vehicle Availability Breakdown</div>
                <div className="exec-three-col">
                  <div className="exec-panel">
                    <div className="exec-panel__head">
                      <h3>Category Demand</h3>
                      <span>Bookings by category</span>
                    </div>
                    <div className="exec-panel__body">
                      {categoryDemand.length ? (
                        <div className="analytics-bar-list">
                          {categoryDemand.map((item) => (
                            <DataBar
                              key={item.category}
                              label={item.category}
                              value={item.bookings}
                              max={getMax(categoryDemand, "bookings")}
                              meta={`${formatCurrency(item.revenue)} revenue`}
                            />
                          ))}
                        </div>
                      ) : (
                        <EmptyState>No category data for this period.</EmptyState>
                      )}
                    </div>
                  </div>

                  <div className="exec-panel">
                    <div className="exec-panel__head">
                      <h3>Top Booked Vehicles</h3>
                      <span>Top performing units</span>
                    </div>
                    <div className="exec-panel__body">
                      {mostBookedVehicles.length ? (
                        <div className="analytics-bar-list">
                          {mostBookedVehicles.map((item) => (
                            <DataBar
                              key={item.vehicleId}
                              label={item.vehicleName}
                              value={item.bookings}
                              max={getMax(mostBookedVehicles, "bookings")}
                              meta={`${item.category} · ${formatPercent(item.utilizationRate)} utilization`}
                            />
                          ))}
                        </div>
                      ) : (
                        <EmptyState>No booking data for this period.</EmptyState>
                      )}
                    </div>
                  </div>

                  <div className="exec-panel">
                    <div className="exec-panel__head">
                      <h3>Underused Vehicles</h3>
                      <span>Units needing attention</span>
                    </div>
                    <div className="exec-panel__body">
                      {underutilizedVehicles.length ? (
                        <div className="exec-mini-list">
                          {underutilizedVehicles.map((item) => (
                            <div key={item.vehicleId} className="exec-mini-list__row">
                              <strong>{item.vehicleName}</strong>
                              <span>{formatPercent(item.utilizationRate)} · {formatNumber(item.bookings)} bookings</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <EmptyState>No low-utilization vehicles for this period.</EmptyState>
                      )}
                    </div>
                  </div>
                </div>

              </section>
            ) : null}

            {activeTab === "customers" ? (
              <section className="analytics-section exec-tab">

                <div className="exec-tab-header">
                  <div>
                    <span className="exec-eyebrow">Customer Insights</span>
                    <h2>Customers</h2>
                  </div>
                  <span className="exec-tab-header__meta">{rangeLabel}</span>
                </div>

                <div className="exec-kpi-row">
                  {[
                    { label: "New Customers", value: formatNumber(customerVerification.newCustomers ?? customerInsights.newCustomers) },
                    { label: "Fully Verified", value: formatNumber(customerVerification.fullyVerified ?? customerInsights.fullyVerifiedCustomers), good: true },
                    { label: "Pending Verification", value: formatNumber(customerVerification.pending ?? customerInsights.pendingVerification), alert: Number(customerVerification.pending ?? customerInsights.pendingVerification) > 0 },
                    { label: "Expiring Soon", value: formatNumber(customerVerification.expiringSoon ?? customerInsights.expiringSoon), alert: Number(customerVerification.expiringSoon ?? customerInsights.expiringSoon) > 0 },
                    { label: "Repeat Customers", value: formatNumber(customerVerification.repeatCustomers ?? customerInsights.repeatCustomers) },
                  ].map((kpi) => (
                    <div key={kpi.label} className={`exec-kpi-row__item${kpi.alert ? " exec-kpi-row__item--alert" : kpi.good ? " exec-kpi-row__item--good" : ""}`}>
                      <span>{kpi.label}</span>
                      <strong>{kpi.value}</strong>
                    </div>
                  ))}
                </div>

                <div className="exec-section-label">Verification Health</div>
                <div className="exec-two-col">
                  <div className="exec-panel">
                    <div className="exec-panel__head">
                      <h3>Verification Status</h3>
                      <span>Aggregated customer statuses</span>
                    </div>
                    <div className="exec-panel__body">
                      <DonutChart items={verificationItems} />
                    </div>
                  </div>

                  <div className="exec-panel">
                    <div className="exec-panel__head">
                      <h3>Top Customers</h3>
                      <span>By booking count · aggregate only</span>
                    </div>
                    <div className="exec-panel__body">
                      {toArray(customerVerification.topCustomers || customerInsights.topCustomers).length ? (
                        <div className="exec-mini-list">
                          {toArray(customerVerification.topCustomers || customerInsights.topCustomers).map((customer) => (
                            <div key={customer.customerName} className="exec-mini-list__row">
                              <strong>{customer.customerName}</strong>
                              <span>{formatNumber(customer.bookings)} bookings</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <EmptyState>No customer ranking available for this period.</EmptyState>
                      )}
                    </div>
                  </div>
                </div>

                {Number(customerVerification.rejected ?? customerInsights.rejectedVerification) > 0 ||
                 Number(customerVerification.expired ?? customerInsights.expired) > 0 ? (
                  <>
                    <div className="exec-section-label">Verification Concerns</div>
                    <div className="exec-kpi-row">
                      <div className="exec-kpi-row__item exec-kpi-row__item--alert">
                        <span>Rejected</span>
                        <strong>{formatNumber(customerVerification.rejected ?? customerInsights.rejectedVerification)}</strong>
                      </div>
                      <div className="exec-kpi-row__item exec-kpi-row__item--alert">
                        <span>Expired</span>
                        <strong>{formatNumber(customerVerification.expired ?? customerInsights.expired)}</strong>
                      </div>
                    </div>
                  </>
                ) : null}

              </section>
            ) : null}

            {activeTab === "routes" ? (
              <section className="analytics-section exec-tab">

                <div className="exec-tab-header">
                  <div>
                    <span className="exec-eyebrow">Route & Destination Demand</span>
                    <h2>Routes</h2>
                  </div>
                  <span className="exec-tab-header__meta">{rangeLabel}</span>
                </div>

                {routeInsights.hasDestinationData ? (
                  <>
                    <div className="exec-kpi-row">
                      {[
                        { label: "Long-Distance Bookings", value: formatNumber(routeInsights.longDistanceCount) },
                        { label: "Avg Trip Duration", value: formatHours(routeInsights.averageTripDurationHours) },
                        { label: "Inter-Island / Special", value: formatNumber(routeInsights.interIslandCount), alert: Number(routeInsights.interIslandCount) > 0 },
                        { label: "Route Review Needed", value: formatNumber(routeInsights.specialRouteReviewCount), alert: Number(routeInsights.specialRouteReviewCount) > 0 },
                        { label: "Restricted Area Attempts", value: formatNumber(routeInsights.restrictedAreaAttempts), alert: Number(routeInsights.restrictedAreaAttempts) > 0 },
                      ].map((kpi) => (
                        <div key={kpi.label} className={`exec-kpi-row__item${kpi.alert ? " exec-kpi-row__item--alert" : ""}`}>
                          <span>{kpi.label}</span>
                          <strong>{kpi.value}</strong>
                        </div>
                      ))}
                    </div>

                    <div className="exec-section-label">Destination Demand</div>
                    <div className="exec-panel">
                      <div className="exec-panel__head">
                        <h3>Top Destinations</h3>
                        <span>Bookings by destination</span>
                      </div>
                      <div className="exec-panel__body">
                        {toArray(routeInsights.topDestinations).length ? (
                          <div className="analytics-bar-list">
                            {toArray(routeInsights.topDestinations).map((item) => (
                              <DataBar
                                key={item.destination}
                                label={item.destination}
                                value={item.bookings}
                                max={getMax(toArray(routeInsights.topDestinations), "bookings")}
                                meta={`${formatCurrency(item.revenue)} revenue · ${formatNumber(item.longDistance)} long-distance`}
                                fullLabel
                              />
                            ))}
                          </div>
                        ) : (
                          <EmptyState>Destination data will appear once more bookings are recorded.</EmptyState>
                        )}
                      </div>
                    </div>

                    {Number(routeInsights.specialRouteReviewCount) > 0 ? (
                      <>
                        <div className="exec-section-label">Operational Signals</div>
                        <div className="exec-insight-card exec-insight-card--warning">
                          <div className="exec-insight-card__top">
                            <span className="exec-insight-priority exec-insight-priority--warning">Attention Required</span>
                            <span className="exec-insight-category">Routes</span>
                          </div>
                          <h4 className="exec-insight-card__title">Special Route Review Pending</h4>
                          <p className="exec-insight-card__body">
                            {formatNumber(routeInsights.specialRouteReviewCount)} booking{Number(routeInsights.specialRouteReviewCount) !== 1 ? "s" : ""} with inter-island, manual, or special route arrangements require admin review.
                          </p>
                          <div className="exec-insight-card__action">
                            <span>Suggested Action</span>
                            <p>Open Booking Management and filter by route review to resolve pending items.</p>
                          </div>
                        </div>
                      </>
                    ) : null}
                  </>
                ) : (
                  <div className="exec-empty-state">
                    <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 13l4.553 2.276A1 1 0 0021 21.382V10.618a1 1 0 00-1.447-.894L15 12m0 8V12m0 0L9 7" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <p>Route insights will appear once more bookings include destination data.</p>
                  </div>
                )}

              </section>
            ) : null}

            {activeTab === "operations" ? (
              <section className="analytics-section exec-tab">

                <div className="exec-tab-header">
                  <div>
                    <span className="exec-eyebrow">Operations Health</span>
                    <h2>Operations</h2>
                  </div>
                  <span className="exec-tab-header__meta">{rangeLabel}</span>
                </div>

                <div className="exec-kpi-row">
                  {queueItems.slice(0, 5).map((item) => (
                    <div key={item.key || item.label} className={`exec-kpi-row__item${Number(item.count) > 0 ? " exec-kpi-row__item--alert" : ""}`}>
                      <span>{item.label}</span>
                      <strong>{formatNumber(item.count)}</strong>
                    </div>
                  ))}
                </div>

                <div className="exec-section-label">Action Queue</div>
                <div className="exec-panel">
                  <div className="exec-panel__head">
                    <h3>Pending Work Items</h3>
                    <span>Requires admin or staff action</span>
                  </div>
                  <div className="exec-workload-list">
                    {queueItems.map((item) => (
                      <div key={item.key || item.label} className="exec-workload-row">
                        <div className="exec-workload-row__body">
                          <strong>{item.label}</strong>
                          {item.description ? <p className="exec-ops-desc">{item.description}</p> : null}
                        </div>
                        <div className="exec-workload-row__right">
                          <span className={`exec-workload-row__count${Number(item.count) > 0 ? " exec-workload-row__count--alert" : ""}`}>
                            {formatNumber(item.count)}
                          </span>
                          {item.href || item.key ? (
                            <button
                              type="button"
                              className="exec-workload-row__open"
                              onClick={() => openQueueTarget(item)}
                            >
                              Open
                            </button>
                          ) : null}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

              </section>
            ) : null}

            {activeTab === "recommendations" ? (
              <section className="analytics-section exec-tab">

                <div className="exec-tab-header">
                  <div>
                    <span className="exec-eyebrow">Management Recommendations</span>
                    <h2>Recommendations</h2>
                  </div>
                  <span className="exec-tab-header__meta">{rangeLabel}</span>
                </div>

                {recommendations.length ? (
                  <>
                    <div className="exec-recommendation-summary">
                      <p>Recommendations are ranked by urgency and business impact, with clear next steps for admin action.</p>
                      <div className="exec-recommendation-summary__counts" aria-label="Recommendation priority counts">
                        <span className="exec-recommendation-count exec-recommendation-count--danger">High: {recommendationCounts.high}</span>
                        <span className="exec-recommendation-count exec-recommendation-count--warning">Medium: {recommendationCounts.medium}</span>
                        <span className="exec-recommendation-count exec-recommendation-count--info">Low: {recommendationCounts.low}</span>
                      </div>
                    </div>
                    <div className="exec-section-label">Priority-ranked decisions</div>
                    <div className="exec-insights-grid exec-insights-grid--full">
                      {recommendations.map((item, index) => (
                        <RecommendationCard
                          key={`${item.type || item.category || "recommendation"}-${item.title}-${index}`}
                          item={item}
                          onAction={openRecommendationAction}
                        />
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="exec-empty-state">
                    <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <p>No recommendations yet. FleetX will generate insights once more booking activity is available for this period.</p>
                  </div>
                )}

              </section>
            ) : null}

            {activeTab === "reports" ? (
              <section className="analytics-section exec-tab">

                <div className="exec-tab-header">
                  <div>
                    <span className="exec-eyebrow">Executive Reports</span>
                    <h2>Reports</h2>
                  </div>
                  <span className="exec-tab-header__meta">{rangeLabel}</span>
                </div>

                {exportNotice ? (
                  <div className="analytics-state analytics-state--success">{exportNotice}</div>
                ) : null}
                {exportError ? (
                  <div className="analytics-state analytics-state--error">{exportError}</div>
                ) : null}

                <div className="exec-section-label">Available Reports</div>
                <div className="exec-reports-full-grid">
                  {reportCards.map((report) => (
                    <article key={report.id} className="exec-report-full-card">
                      <div className="exec-report-full-card__body">
                        <h4>{report.name}</h4>
                        <p>{report.description}</p>
                        {report.noData ? (
                          <span className="exec-report-full-card__nodata">No data for this period</span>
                        ) : null}
                        <div className="exec-report-full-card__stats">
                          {report.numbers.map(([label, value]) => (
                            <div key={label} className="exec-report-card__stat">
                              <span>{label}</span>
                              <strong>{value ?? 0}</strong>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="exec-report-full-card__actions">
                        <button
                          type="button"
                          className="exec-report-btn exec-report-btn--pdf"
                          onClick={() => handleExportPdf(report)}
                          disabled={!canExportReports || exportDisabledForRange || exportingPdfReport === report.id}
                        >
                          {exportingPdfReport === report.id ? "Generating…" : "Download PDF"}
                        </button>
                        <button
                          type="button"
                          className="exec-report-btn exec-report-btn--csv"
                          onClick={() => handleExportReport(report)}
                          disabled={!canExportReports || exportDisabledForRange || exportingReport === report.id}
                        >
                          {exportingReport === report.id ? "Exporting…" : "Export CSV"}
                        </button>
                        {!canExportReports ? (
                          <span className="exec-report-full-card__hint">Available to Admin only</span>
                        ) : exportDisabledForRange ? (
                          <span className="exec-report-full-card__hint">Select a date range first</span>
                        ) : null}
                      </div>
                    </article>
                  ))}
                </div>

              </section>
            ) : null}
          </>
        ) : null}
      </div>
    </AdminShell>
  );
}
